import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Users } from "lucide-react";
import { LEAD_SOURCES, LEAD_SCORES } from "@/types/crm";
import type { LeadScore, LeadSource } from "@/types/crm";
import { validatePhone, validateCanadianPostalCode } from "@/utils/validation";
import { logAudit } from "@/utils/auditLog";
import { useAuth } from "@/hooks/useAuth";

const CANADIAN_PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" }
];

interface AddCustomerDialogProps {
  onCreated: () => void;
  trigger?: React.ReactNode;
  onNavigate?: (customerId: string) => void;
}

export function AddCustomerDialog({ onCreated, trigger, onNavigate }: AddCustomerDialogProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customers, setCustomers] = useState<{id: string, first_name: string, last_name: string}[]>([]);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    province: "ON",
    postal_code: "",
    date_of_birth: "",
    lead_source: "website" as LeadSource,
    lead_score: "warm" as LeadScore,
    credit_rating: "",
    referred_by: "none",
    driver_license: "",
    driver_license_expiry: "",
  });

  useEffect(() => {
    if (open) {
      loadCustomers();
    }
  }, [open]);

  const loadCustomers = async () => {
    const { data } = await supabase
      .from("customers")
      .select("id, first_name, last_name")
      .is("deleted_at", null)
      .order("last_name", { ascending: true });
    if (data) setCustomers(data);
  };

  const resetForm = () => {
    setForm({
      first_name: "", last_name: "", email: "", phone: "",
      address: "", city: "", province: "ON", postal_code: "",
      date_of_birth: "", lead_source: "website", lead_score: "warm",
      credit_rating: "", referred_by: "none",
      driver_license: "", driver_license_expiry: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("First and last name are required");
      return;
    }

    const emailVal = form.email.trim();
    const phoneVal = form.phone.trim();
    const postalVal = form.postal_code.trim();

    if (emailVal) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
        toast.error("Please enter a valid email address");
        return;
      }
      setIsSubmitting(true);
      // Email uniqueness check (MED-4)
      const { data: existingEmail } = await supabase
        .from("customers")
        .select("id")
        .eq("email", emailVal)
        .is("deleted_at", null)
        .maybeSingle();

      if (existingEmail) {
        toast.error("A customer with this email address already exists.");
        setIsSubmitting(false);
        return;
      }
    } else {
      setIsSubmitting(true);
    }

    if (phoneVal && !validatePhone(phoneVal)) {
      toast.error("Please enter a valid 10-digit phone number");
      setIsSubmitting(false);
      return;
    }

    if (postalVal && !validateCanadianPostalCode(postalVal)) {
      toast.error("Please enter a valid Canadian postal code (e.g., M5V 1A1)");
      setIsSubmitting(false);
      return;
    }

    const { data: newCust, error } = await supabase.from("customers").insert({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: emailVal || null,
      phone: phoneVal ? phoneVal.replace(/\D/g, '') : null,
      address: form.address.trim() || null,
      city: form.city.trim() || null,
      province: form.province.trim() || null,
      postal_code: postalVal ? postalVal.toUpperCase() : null,
      date_of_birth: form.date_of_birth || null,
      lead_source: form.lead_source,
      lead_score: form.lead_score,
      credit_rating: form.credit_rating.trim() || null,
      referred_by: form.referred_by === "none" ? null : form.referred_by,
      driver_license: form.driver_license.trim() || null,
      driver_license_expiry: form.driver_license_expiry || null,
    } as any).select('id').single();

    if (error) {
      toast.error("Failed to create customer");
      console.error(error);
    } else {
      toast.success("Customer created successfully");
      
      if (newCust?.id) {
        await logAudit({
          entityType: 'customer',
          entityId: newCust.id,
          action: 'created',
          metadata: {
            name: `${form.first_name.trim()} ${form.last_name.trim()}`,
            email: emailVal || null,
            phone: phoneVal || null,
            source: 'manual_add',
          },
          performedBy: user?.id,
        });
      }
      
      resetForm();
      setOpen(false);
      onCreated();
      if (onNavigate && newCust?.id) onNavigate(newCust.id);
    }
    setIsSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Customer
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Customer</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-first">First Name *</Label>
              <Input id="add-first" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-last">Last Name *</Label>
              <Input id="add-last" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} required />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-email">Email</Label>
              <Input id="add-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-phone">Phone</Label>
              <Input id="add-phone" type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="(416) 555-1234" />
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-address">Address</Label>
              <Input id="add-address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Referred By</Label>
              <Select value={form.referred_by} onValueChange={(v) => setForm({ ...form, referred_by: v })}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="No referral" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  <SelectItem value="none">No referral</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.first_name} {c.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-city">City</Label>
              <Input id="add-city" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Province</Label>
              <Select value={form.province} onValueChange={(v) => setForm({ ...form, province: v })}>
                <SelectTrigger id="add-province"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CANADIAN_PROVINCES.map(p => (
                    <SelectItem key={p.code} value={p.code}>{p.code} - {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-postal">Postal Code</Label>
              <Input id="add-postal" value={form.postal_code} onChange={e => setForm({ ...form, postal_code: e.target.value })} placeholder="M5V 1A1" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-dl">Driver's License No.</Label>
              <Input id="add-dl" value={form.driver_license} onChange={e => setForm({ ...form, driver_license: e.target.value })} placeholder="e.g. R4320-40027-50304" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-dl-expiry">Driver's License Expiry</Label>
              <Input id="add-dl-expiry" type="date" value={form.driver_license_expiry} onChange={e => setForm({ ...form, driver_license_expiry: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-dob">Date of Birth</Label>
              <Input id="add-dob" type="date" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Lead Source</Label>
              <Select value={form.lead_source} onValueChange={(v) => setForm({ ...form, lead_source: v as LeadSource })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Lead Score</Label>
              <Select value={form.lead_score} onValueChange={(v) => setForm({ ...form, lead_score: v as LeadScore })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAD_SCORES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Customer"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
